import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CrossIcon } from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './HistoryPeriodSheet.css';

const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const PAST_MONTH_COUNT = 12;

function parseDateKey(value) {
  if (typeof value !== 'string') return null;

  const parsedDate = dayjs(value);
  if (!parsedDate.isValid() || parsedDate.format(DATE_KEY_FORMAT) !== value) {
    return null;
  }

  return parsedDate.startOf('day');
}

function parseBoundaryDate(value) {
  if (!value) return null;

  const parsedDate = dayjs(value);

  return parsedDate.isValid() ? parsedDate.startOf('day') : null;
}

function resolveEarliestDate(minDate, today) {
  const parsedMinDate = parseBoundaryDate(minDate);

  if (!parsedMinDate) {
    return today.startOf('month').subtract(PAST_MONTH_COUNT, 'month');
  }

  return parsedMinDate.isAfter(today, 'day') ? today : parsedMinDate;
}

function normalizeRange(value, earliestDate, today) {
  const rawStart = parseDateKey(value?.start);
  const rawEnd = parseDateKey(value?.end);

  if (!rawStart || !rawEnd) {
    return null;
  }

  const orderedStart = rawStart.isAfter(rawEnd, 'day') ? rawEnd : rawStart;
  const orderedEnd = rawStart.isAfter(rawEnd, 'day') ? rawStart : rawEnd;

  if (orderedEnd.isBefore(earliestDate, 'day') || orderedStart.isAfter(today, 'day')) {
    return null;
  }

  const start = orderedStart.isBefore(earliestDate, 'day')
    ? earliestDate
    : orderedStart;
  const end = orderedEnd.isAfter(today, 'day') ? today : orderedEnd;

  return {
    start: start.format(DATE_KEY_FORMAT),
    end: end.format(DATE_KEY_FORMAT),
    mode: value?.mode === 'month' ? 'month' : 'range',
  };
}

function formatDate(date, language, options) {
  try {
    return new Intl.DateTimeFormat(language || undefined, options).format(date.toDate());
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date.toDate());
  }
}

function buildMonths(earliestDate, today) {
  const firstMonth = earliestDate.startOf('month');
  const monthCount = today.startOf('month').diff(firstMonth, 'month') + 1;

  return Array.from({ length: monthCount }, (_, index) => firstMonth.add(index, 'month'));
}

function buildMonthCells(month) {
  const leadingCellCount = (month.startOf('month').day() + 6) % 7;
  const daysInMonth = month.daysInMonth();

  return [
    ...Array.from({ length: leadingCellCount }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => month.date(index + 1)),
  ];
}

function HistoryPeriodSheet({
  value,
  minDate,
  isUserSelection = false,
  onApply,
  onClose,
}) {
  const { common, history, language, t } = useContext(TranslationContext);
  const today = useMemo(() => dayjs().startOf('day'), []);
  const earliestDate = useMemo(
    () => resolveEarliestDate(minDate, today),
    [minDate, today]
  );
  const months = useMemo(
    () => buildMonths(earliestDate, today),
    [earliestDate, today]
  );
  const normalizedValue = useMemo(
    () => normalizeRange(value, earliestDate, today),
    [earliestDate, today, value]
  );
  const [draft, setDraft] = useState(normalizedValue);
  const [activeEndpoint, setActiveEndpoint] = useState(normalizedValue ? null : 'start');
  const [focusedDateKey, setFocusedDateKey] = useState(
    normalizedValue?.start || today.format(DATE_KEY_FORMAT)
  );
  const dialogContentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const calendarScrollRef = useRef(null);
  const monthRefs = useRef({});
  const dayRefs = useRef({});
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);

  const selectedStart = parseDateKey(draft?.start);
  const selectedEnd = parseDateKey(draft?.end);
  const hasCompleteRange = Boolean(selectedStart && selectedEnd);
  const selectedMonth = (selectedStart || today).startOf('month');
  const previousMonth = selectedMonth.subtract(1, 'month');
  const initialMonthKey = (
    previousMonth.isBefore(earliestDate, 'month') ? selectedMonth : previousMonth
  ).format('YYYY-MM');

  const dateFormatterOptions = useMemo(
    () => ({ day: 'numeric', month: 'long' }),
    []
  );
  const dateWithYearFormatterOptions = useMemo(
    () => ({ day: 'numeric', month: 'long', year: 'numeric' }),
    []
  );
  const accessibleDateFormatterOptions = useMemo(
    () => ({ day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }),
    []
  );
  const monthFormatterOptions = useMemo(
    () => ({ month: 'long', year: 'numeric' }),
    []
  );

  const weekdays = useMemo(() => {
    if (Array.isArray(history.weekdaysShort)) {
      return history.weekdaysShort.slice(0, 7);
    }

    return [];
  }, [history.weekdaysShort]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = useCallback(() => {
    triggerImpactHaptic('light');
    onCloseRef.current();
  }, []);

  const scrollToInitialMonth = useCallback(() => {
    const scrollContainer = calendarScrollRef.current;
    const targetMonth = monthRefs.current[initialMonthKey]
      || monthRefs.current[today.format('YYYY-MM')];

    if (!scrollContainer || !targetMonth) return;

    const top = Math.max(0, targetMonth.offsetTop - 8);

    if (typeof scrollContainer.scrollTo === 'function') {
      scrollContainer.scrollTo({ top, behavior: 'auto' });
    } else {
      scrollContainer.scrollTop = top;
    }
  }, [initialMonthKey, today]);

  useEffect(() => {
    setDraft(normalizedValue);
    setActiveEndpoint(normalizedValue ? null : 'start');
    setFocusedDateKey(normalizedValue?.start || today.format(DATE_KEY_FORMAT));
  }, [normalizedValue, today]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    previousFocusRef.current = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });

    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        dialogContentRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element.tabIndex >= 0);

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleDocumentKeyDown);

      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus?.({ preventScroll: true });
      }
    };
  }, [handleClose]);

  const handleEndpointChange = (endpoint) => {
    triggerImpactHaptic('light');
    setActiveEndpoint(endpoint === 'end' && !selectedStart ? 'start' : endpoint);
  };

  const handleDaySelect = (date) => {
    if (date.isBefore(earliestDate, 'day') || date.isAfter(today, 'day')) return;

    const nextDateKey = date.format(DATE_KEY_FORMAT);
    triggerImpactHaptic('light');
    setFocusedDateKey(nextDateKey);

    if (activeEndpoint === 'end' && selectedStart) {
      if (date.isBefore(selectedStart, 'day')) {
        setDraft({
          start: nextDateKey,
          end: selectedStart.format(DATE_KEY_FORMAT),
          mode: 'range',
        });
      } else {
        setDraft({
          start: selectedStart.format(DATE_KEY_FORMAT),
          end: nextDateKey,
          mode: 'range',
        });
      }
      setActiveEndpoint(null);
      return;
    }

    if (activeEndpoint === 'start' && selectedEnd && !date.isAfter(selectedEnd, 'day')) {
      setDraft({
        start: nextDateKey,
        end: selectedEnd.format(DATE_KEY_FORMAT),
        mode: 'range',
      });
      setActiveEndpoint(null);
      return;
    }

    setDraft({ start: nextDateKey, end: null, mode: 'range' });
    setActiveEndpoint('end');
  };

  const handleMonthSelect = (month) => {
    if (month.isAfter(today, 'month')) return;

    const monthStart = month.isSame(earliestDate, 'month')
      ? earliestDate
      : month.startOf('month');
    const monthEnd = month.isSame(today, 'month') ? today : month.endOf('month');
    const nextRange = {
      start: monthStart.format(DATE_KEY_FORMAT),
      end: monthEnd.format(DATE_KEY_FORMAT),
      mode: 'month',
    };

    triggerImpactHaptic('light');
    setDraft(nextRange);
    setFocusedDateKey(nextRange.start);
    setActiveEndpoint(null);
  };

  const focusDate = (date) => {
    if (date.isBefore(earliestDate, 'day') || date.isAfter(today, 'day')) return;

    const dateKey = date.format(DATE_KEY_FORMAT);
    setFocusedDateKey(dateKey);

    window.requestAnimationFrame(() => {
      const dayButton = dayRefs.current[dateKey];
      dayButton?.focus({ preventScroll: true });
      dayButton?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  const handleDayKeyDown = (event, date) => {
    const weekdayOffset = (date.day() + 6) % 7;
    let nextDate = null;

    switch (event.key) {
      case 'ArrowLeft':
        nextDate = date.subtract(1, 'day');
        break;
      case 'ArrowRight':
        nextDate = date.add(1, 'day');
        break;
      case 'ArrowUp':
        nextDate = date.subtract(7, 'day');
        break;
      case 'ArrowDown':
        nextDate = date.add(7, 'day');
        break;
      case 'Home':
        nextDate = date.subtract(weekdayOffset, 'day');
        break;
      case 'End':
        nextDate = date.add(6 - weekdayOffset, 'day');
        break;
      case 'PageUp':
        nextDate = date.subtract(1, 'month');
        break;
      case 'PageDown':
        nextDate = date.add(1, 'month');
        break;
      default:
        return;
    }

    event.preventDefault();

    if (nextDate.isAfter(today, 'day')) {
      focusDate(today);
      return;
    }

    if (nextDate.isBefore(earliestDate, 'day')) {
      focusDate(earliestDate);
      return;
    }

    focusDate(nextDate);
  };

  const handleApply = () => {
    if (!hasCompleteRange) return;

    triggerImpactHaptic('medium');
    onApply({
      start: selectedStart.format(DATE_KEY_FORMAT),
      end: selectedEnd.format(DATE_KEY_FORMAT),
      mode: draft.mode === 'month' ? 'month' : 'range',
    });
  };

  const handleReset = () => {
    triggerImpactHaptic('light');
    onApply(null);
  };

  return (
    <BottomSheet
      className="history-period-sheet"
      panelClassName="history-period-sheet__panel"
      titleId="history-period-sheet-title"
      onClose={handleClose}
      onOpenComplete={scrollToInitialMonth}
    >
      <div ref={dialogContentRef} className="history-period-sheet__content">
        <header className="history-period-sheet__header">
          <h2 id="history-period-sheet-title" className="history-period-sheet__title">
            {history.selectPeriod}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="history-period-sheet__close"
            aria-label={common.close}
            onClick={handleClose}
          >
            <CrossIcon className="history-period-sheet__close-icon" />
          </button>
        </header>

        <div className="history-period-sheet__fields">
          <button
            type="button"
            className={[
              'history-period-sheet__field',
              activeEndpoint === 'start' ? 'history-period-sheet__field--active' : '',
            ].filter(Boolean).join(' ')}
            aria-pressed={activeEndpoint === 'start'}
            onClick={() => handleEndpointChange('start')}
          >
            <span className="history-period-sheet__field-label">{history.periodStart}</span>
            <span
              className={[
                'history-period-sheet__field-value',
                !selectedStart ? 'history-period-sheet__field-value--placeholder' : '',
              ].filter(Boolean).join(' ')}
            >
              {selectedStart
                ? formatDate(
                  selectedStart,
                  language,
                  selectedStart.isSame(today, 'year')
                    ? dateFormatterOptions
                    : dateWithYearFormatterOptions
                )
                : history.chooseDate}
            </span>
          </button>

          <button
            type="button"
            className={[
              'history-period-sheet__field',
              activeEndpoint === 'end' ? 'history-period-sheet__field--active' : '',
            ].filter(Boolean).join(' ')}
            aria-pressed={activeEndpoint === 'end'}
            onClick={() => handleEndpointChange('end')}
          >
            <span className="history-period-sheet__field-label">{history.periodEnd}</span>
            <span
              className={[
                'history-period-sheet__field-value',
                !selectedEnd ? 'history-period-sheet__field-value--placeholder' : '',
              ].filter(Boolean).join(' ')}
            >
              {selectedEnd
                ? formatDate(
                  selectedEnd,
                  language,
                  selectedEnd.isSame(today, 'year')
                    ? dateFormatterOptions
                    : dateWithYearFormatterOptions
                )
                : history.chooseEndDate}
            </span>
          </button>

          {isUserSelection && normalizedValue?.mode === 'month' ? (
            <button
              type="button"
              className="history-period-sheet__reset"
              onClick={handleReset}
            >
              {history.resetPeriod}
            </button>
          ) : null}
        </div>

        <div className="history-period-sheet__weekdays" aria-hidden="true">
          {Array.from({ length: 7 }, (_, index) => (
            <span key={index} className="history-period-sheet__weekday">
              {weekdays[index] || ''}
            </span>
          ))}
        </div>

        <div ref={calendarScrollRef} className="history-period-sheet__calendar-scroll">
          <div className="history-period-sheet__calendar">
            {months.map((month) => {
              const monthKey = month.format('YYYY-MM');
              const monthLabel = formatDate(month, language, monthFormatterOptions);
              const availableMonthStart = month.isSame(earliestDate, 'month')
                ? earliestDate
                : month.startOf('month');
              const availableMonthEnd = month.isSame(today, 'month')
                ? today
                : month.endOf('month');
              const isWholeMonthSelected = draft?.mode === 'month'
                && draft.start === availableMonthStart.format(DATE_KEY_FORMAT)
                && draft.end === availableMonthEnd.format(DATE_KEY_FORMAT);

              return (
                <section
                  key={monthKey}
                  ref={(node) => {
                    if (node) monthRefs.current[monthKey] = node;
                  }}
                  className="history-period-sheet__month"
                  aria-labelledby={`history-period-month-${monthKey}`}
                >
                  <h3 className="history-period-sheet__month-heading">
                    <button
                      id={`history-period-month-${monthKey}`}
                      type="button"
                      className={[
                        'history-period-sheet__month-button',
                        isWholeMonthSelected
                          ? 'history-period-sheet__month-button--selected'
                          : '',
                      ].filter(Boolean).join(' ')}
                      aria-label={t(
                        'history.wholeMonthAria',
                        { month: monthLabel },
                        history.wholeMonthAria
                      )}
                      aria-pressed={isWholeMonthSelected}
                      onClick={() => handleMonthSelect(month)}
                    >
                      {monthLabel}
                    </button>
                  </h3>

                  <div className="history-period-sheet__days">
                    {buildMonthCells(month).map((date, cellIndex) => {
                      if (!date) {
                        return (
                          <span
                            key={`empty-${cellIndex}`}
                            className="history-period-sheet__day-cell"
                            aria-hidden="true"
                          />
                        );
                      }

                      const dateKey = date.format(DATE_KEY_FORMAT);
                      const isDisabled = date.isBefore(earliestDate, 'day')
                        || date.isAfter(today, 'day');
                      const isRangeStart = dateKey === draft?.start;
                      const isRangeEnd = dateKey === draft?.end;
                      const isInRange = hasCompleteRange
                        && !date.isBefore(selectedStart, 'day')
                        && !date.isAfter(selectedEnd, 'day');
                      const isSingleDay = isRangeStart && isRangeEnd;
                      const isToday = date.isSame(today, 'day');

                      return (
                        <span
                          key={dateKey}
                          className={[
                            'history-period-sheet__day-cell',
                            isInRange ? 'history-period-sheet__day-cell--in-range' : '',
                            isRangeStart ? 'history-period-sheet__day-cell--range-start' : '',
                            isRangeEnd ? 'history-period-sheet__day-cell--range-end' : '',
                            isSingleDay ? 'history-period-sheet__day-cell--single' : '',
                            date.day() === 1 ? 'history-period-sheet__day-cell--week-start' : '',
                            date.day() === 0 ? 'history-period-sheet__day-cell--week-end' : '',
                            date.date() === 1 ? 'history-period-sheet__day-cell--month-start' : '',
                            date.date() === date.daysInMonth()
                              ? 'history-period-sheet__day-cell--month-end'
                              : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <button
                            ref={(node) => {
                              if (node) dayRefs.current[dateKey] = node;
                            }}
                            type="button"
                            className={[
                              'history-period-sheet__day',
                              isRangeStart || isRangeEnd
                                ? 'history-period-sheet__day--selected'
                                : '',
                              isToday ? 'history-period-sheet__day--today' : '',
                            ].filter(Boolean).join(' ')}
                            disabled={isDisabled}
                            tabIndex={!isDisabled && focusedDateKey === dateKey ? 0 : -1}
                            aria-label={formatDate(
                              date,
                              language,
                              accessibleDateFormatterOptions
                            )}
                            aria-pressed={isInRange || isRangeStart}
                            aria-current={isToday ? 'date' : undefined}
                            onClick={() => handleDaySelect(date)}
                            onKeyDown={(event) => handleDayKeyDown(event, date)}
                          >
                            {date.date()}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        <div className="history-period-sheet__action-wrap">
          <button
            type="button"
            className="history-period-sheet__action"
            disabled={!hasCompleteRange}
            onClick={handleApply}
          >
            {common.done}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default HistoryPeriodSheet;
